import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Sparkles, Clock } from "lucide-react";
import { useIrisAvatar } from "../../hooks/useIrisAvatar";

interface MobileFloatingChatIrisProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const MobileFloatingChatIris: React.FC<MobileFloatingChatIrisProps> = ({
  className = "",
  onClose,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const { avatarUrl } = useIrisAvatar();

  useEffect(() => {
    // Fermer si le parent tente d'ouvrir le chat
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    const tooltipTimer = setTimeout(() => setShowTooltip(true), 3000);
    const hideTimer = setTimeout(() => setShowTooltip(false), 8000);

    return () => {
      clearTimeout(tooltipTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  const handleComingSoon = () => {
    setShowComingSoon(true);
    setShowTooltip(false);
    onClose?.();
    window.setTimeout(() => setShowComingSoon(false), 3000);
  };

  return (
    <div className={`fixed bottom-20 right-4 z-[9999] ${className}`}>
      <AnimatePresence>
        {showTooltip && !showComingSoon && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 w-48"
          >
            <div className="bg-white rounded-lg shadow-lg p-3 border border-gray-200">
              <div className="flex items-start space-x-2">
                <Sparkles className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600">
                  L&apos;assistante IA Iris sera bientôt disponible.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showComingSoon && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-white text-gray-900 text-xs font-semibold px-3 py-2 rounded-xl shadow-lg border border-amber-200"
          >
            Bientôt disponible
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        id="mobile-iris-chat-button"
        onClick={handleComingSoon}
        className="w-10 h-10 rounded-full shadow-md flex items-center justify-center overflow-hidden relative"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
        }}
        aria-label="Chat Iris - Bientôt disponible"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden border border-white/30">
          <img
            src={avatarUrl}
            alt="IRIS"
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
              target.parentElement!.innerHTML =
                '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs">🤖</div>';
            }}
          />
        </div>

        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-400 rounded-full border border-white flex items-center justify-center">
          <Clock className="w-1 h-1 text-white" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <MessageCircle className="w-4 h-4 text-white opacity-40" />
        </div>
      </motion.button>
    </div>
  );
};

export default MobileFloatingChatIris;
